import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import HandleEditor from "@/components/HandleEditor";
import BannerEditor from "@/components/BannerEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Camera, Trash2, Landmark, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AVATAR_MAX_MB = 4;
const ACCEPTED_AVATAR = [".jpg", ".jpeg", ".png", ".webp"];

export default function EditProfile() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [me, setMe] = useState(null);
  const [avatarVer, setAvatarVer] = useState(Date.now());
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    bio: "",
    roles: "",
    skills: "",
    genre_preferences: "",
    available_for_work: false,
    location: "",
    website: "",
    phone_number: "",
  });
  const [loading, setLoading] = useState(false);

  // Payout bank details
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankDirty, setBankDirty] = useState(false);

  const refreshMe = () => api.get("/auth/me").then((r) => {
    setMe(r.data);
    setForm({
      name: r.data.name || "",
      bio: r.data.bio || "",
      roles: (r.data.roles || []).join(", "),
      skills: (r.data.skills || []).join(", "),
      genre_preferences: (r.data.genre_preferences || []).join(", "),
      available_for_work: !!r.data.available_for_work,
      location: r.data.location || "",
      website: r.data.website || "",
      phone_number: r.data.phone_number || "",
    });
    setBankCode(r.data.bank_code || "");
    setAccountNumber(r.data.account_number || "");
    setResolvedName(r.data.account_name || "");
    setBankDirty(false);
  });

  useEffect(() => {
    refreshMe();
    api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Display name must be at least 2 characters");
    setLoading(true);
    try {
      const { data } = await api.patch("/filmmaker/profile", {
        name: form.name.trim(),
        bio: form.bio,
        roles: toArr(form.roles),
        skills: toArr(form.skills),
        genre_preferences: toArr(form.genre_preferences),
        available_for_work: form.available_for_work,
        location: form.location,
        website: form.website,
        phone_number: form.phone_number || null,
      });
      setUser((u) => (u ? { ...u, ...data } : u));
      toast.success("Profile updated");
      navigate("/filmmaker");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const onAvatarPick = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ACCEPTED_AVATAR.some((x) => lower.endsWith(x))) {
      toast.error("Profile picture must be JPG, PNG or WebP.");
      return;
    }
    if (file.size > AVATAR_MAX_MB * 1024 * 1024) {
      toast.error(`Picture is over ${AVATAR_MAX_MB} MB. Please use a smaller image.`);
      return;
    }
    const fd = new FormData();
    fd.append("avatar", file);
    setAvatarUploading(true);
    try {
      await api.post("/filmmaker/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarVer(Date.now());
      await refreshMe();
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setAvatarUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!me?.avatar_path) return;
    try {
      await api.delete("/filmmaker/avatar");
      setAvatarVer(Date.now());
      await refreshMe();
      toast.success("Profile picture removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove");
    }
  };

  // Auto-resolve account name when both bank + 10-digit account number are
  // present and the pair has changed since the last save.
  useEffect(() => {
    if (!bankDirty) return;
    if (!bankCode || accountNumber.length !== 10) {
      setResolvedName("");
      return;
    }
    let cancelled = false;
    setResolving(true);
    setResolvedName("");
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/filmmaker/bank/resolve", {
          bank_code: bankCode,
          account_number: accountNumber,
        });
        if (!cancelled) setResolvedName(data.account_name || "");
      } catch (e) {
        if (!cancelled) {
          setResolvedName("");
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not verify account");
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [bankCode, accountNumber, bankDirty]);

  const saveBank = async () => {
    if (!bankCode || accountNumber.length !== 10) {
      toast.error("Pick a bank and enter a 10-digit account number.");
      return;
    }
    if (!resolvedName) {
      toast.error("Account could not be verified. Double-check the details.");
      return;
    }
    setSavingBank(true);
    try {
      await api.post("/filmmaker/bank", { bank_code: bankCode, account_number: accountNumber });
      toast.success("Payout bank account saved");
      await refreshMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save bank account");
    } finally {
      setSavingBank(false);
    }
  };

  const removeBank = async () => {
    if (!me?.account_number) return;
    if (!window.confirm("Remove your payout bank account? You'll need to add it again before requesting a payout.")) return;
    try {
      await api.delete("/filmmaker/bank");
      await refreshMe();
      toast.success("Bank account removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove");
    }
  };

  const initials = (me?.name || "U")[0].toUpperCase();
  const avatarUrl = me?.avatar_path ? `${API}/users/${me.id}/avatar?v=${avatarVer}` : null;

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-3xl mx-auto pb-20">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Filmmaker</p>
          <h1 className="font-display text-4xl font-black">Edit profile</h1>
          <p className="text-foreground/60 mt-2">Public profile shown on your creator page.</p>
        </div>

        <HandleEditor />
        <BannerEditor />

        {/* AVATAR PANEL */}
        <div className="mb-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-center sm:items-start" data-testid="avatar-panel">
          <div className="relative">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-terracotta to-ember flex items-center justify-center text-4xl font-display font-black text-white border-2 border-white/10">
              {avatarUrl ? (
                <img src={avatarUrl} alt={me?.name} className="w-full h-full object-cover" data-testid="avatar-preview" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            {avatarUploading && (
              <div className="absolute inset-0 rounded-full bg-ink/60 flex items-center justify-center text-xs text-foreground/80">…</div>
            )}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <Label className="text-base">Profile picture</Label>
            <p className="text-xs text-foreground/55 mt-1 leading-relaxed">
              JPG, PNG or WebP · max {AVATAR_MAX_MB} MB · square crop recommended (e.g. 512×512 px) ·
              shown on your public creator profile and across the platform.
            </p>
            <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => { onAvatarPick(e.target.files[0]); e.target.value = ""; }}
                data-testid="avatar-file-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
                className="bg-terracotta hover:bg-ember text-white h-10"
                data-testid="avatar-upload-btn"
              >
                <Camera className="w-4 h-4" />{me?.avatar_path ? "Change picture" : "Upload picture"}
              </Button>
              {me?.avatar_path && (
                <Button
                  type="button"
                  onClick={removeAvatar}
                  variant="outline"
                  className="border-white/15 hover:bg-white/5 h-10"
                  data-testid="avatar-remove-btn"
                >
                  <Trash2 className="w-4 h-4" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="edit-profile-form">
          <div>
            <Label>Display name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={60} placeholder="How your name appears to viewers" className="mt-1.5 bg-ink border-white/10" data-testid="profile-name-input" />
            <p className="text-xs text-foreground/50 mt-1">Shown on your public profile, every film card and your studio.</p>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4} placeholder="Tell viewers and collaborators about yourself…" className="mt-1.5 bg-ink border-white/10" data-testid="profile-bio-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Lagos, Nigeria" className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="mt-1.5 bg-ink border-white/10 h-11" />
            </div>
            <div className="sm:col-span-2">
              <Label>Phone number</Label>
              <Input
                type="tel"
                value={form.phone_number}
                onChange={(e) => set("phone_number", e.target.value)}
                placeholder="+234 800 123 4567 or +1 415 555 0123"
                className="mt-1.5 bg-ink border-white/10 h-11"
                data-testid="profile-phone-input"
              />
              <p className="text-xs text-mist/60 mt-1.5">Include your country code (e.g. +234, +1, +44). Used for payout coordination and account recovery. Not shown publicly.</p>
            </div>
          </div>

          <div>
            <Label>Roles (comma-separated)</Label>
            <Input value={form.roles} onChange={(e) => set("roles", e.target.value)} placeholder="Director, Writer, Producer" className="mt-1.5 bg-ink border-white/10 h-11" data-testid="profile-roles-input" />
          </div>
          <div>
            <Label>Skills (comma-separated)</Label>
            <Input value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="Storyboarding, Color grading, Sound design" className="mt-1.5 bg-ink border-white/10 h-11" />
          </div>
          <div>
            <Label>Preferred genres (comma-separated)</Label>
            <Input value={form.genre_preferences} onChange={(e) => set("genre_preferences", e.target.value)} placeholder="Drama, Documentary" className="mt-1.5 bg-ink border-white/10 h-11" />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-ink/60 border border-white/10">
            <div>
              <p className="font-display font-bold">Available for work</p>
              <p className="text-xs text-foreground/60">Show a green badge on your public profile so collaborators can find you.</p>
            </div>
            <Switch checked={form.available_for_work} onCheckedChange={(v) => set("available_for_work", v)} data-testid="available-toggle" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-ember text-white h-12">
            <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save profile"}
          </Button>
        </form>

        {/* PAYOUT BANK DETAILS */}
        <div className="mt-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="bank-panel">
          <div className="flex items-center gap-2.5 mb-1">
            <Landmark className="w-5 h-5 text-gold" />
            <h2 className="font-display text-xl font-bold">Payout bank account</h2>
          </div>
          <p className="text-xs text-foreground/60 leading-relaxed mb-5">
            We credit your bank account automatically when admin approves a payout. Only Nigerian NUBAN accounts are supported (Paystack Transfers).
          </p>

          {me?.account_number && !bankDirty ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="bank-saved-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </p>
                  <p className="font-display font-bold text-base" data-testid="bank-account-name">{me.account_name}</p>
                  <p className="text-sm text-foreground/75" data-testid="bank-account-number">
                    {me.bank_name} · ••••{me.account_number.slice(-4)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBankDirty(true)}
                    className="border-white/15 hover:bg-white/5 h-10"
                    data-testid="bank-change-btn"
                  >
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={removeBank}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-10"
                    data-testid="bank-remove-btn"
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="bank-form">
              <div>
                <Label>Bank</Label>
                <Select value={bankCode} onValueChange={(v) => { setBankCode(v); setBankDirty(true); }}>
                  <SelectTrigger className="mt-1.5 bg-ink border-white/10 h-11" data-testid="bank-select">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 max-h-72">
                    {banks.map((b) => (
                      <SelectItem key={b.code} value={b.code} data-testid={`bank-option-${b.code}`}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Account number</Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10)); setBankDirty(true); }}
                  placeholder="10-digit NUBAN"
                  inputMode="numeric"
                  maxLength={10}
                  className="mt-1.5 bg-ink border-white/10 h-11 tracking-wider"
                  data-testid="bank-account-input"
                />
              </div>

              <div className="min-h-[56px] rounded-lg bg-ink/60 border border-white/5 p-3 flex items-center gap-2.5" data-testid="bank-resolved">
                {resolving ? (
                  <><Loader2 className="w-4 h-4 animate-spin text-gold" /> <span className="text-sm text-foreground/60">Verifying with bank…</span></>
                ) : resolvedName ? (
                  <><ShieldCheck className="w-4 h-4 text-emerald-400" /> <span className="text-sm text-foreground/90"><span className="text-foreground/60">Account holder:</span> <strong>{resolvedName}</strong></span></>
                ) : (
                  <span className="text-sm text-foreground/50">Pick a bank and enter a 10-digit account number to verify.</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                {me?.account_number && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setBankDirty(false); setBankCode(me.bank_code || ""); setAccountNumber(me.account_number || ""); setResolvedName(me.account_name || ""); }}
                    className="border-white/15 hover:bg-white/5 h-11"
                    data-testid="bank-cancel-btn"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={saveBank}
                  disabled={savingBank || !resolvedName}
                  className="bg-terracotta hover:bg-ember text-white h-11 px-6"
                  data-testid="bank-save-btn"
                >
                  <Save className="w-4 h-4" /> {savingBank ? "Saving…" : "Save bank account"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
